import { useState, useEffect, useContext } from "react";
import { BrPageContext, BrComponentContext, BrManageContentButton } from "@bloomreach/react-sdk";
import "./ProductDetailHeader.scss";
import AddProductExpandable from "../Global/AddProduct/AddProductExpandable";
import HeaderToolbar from "../Common/HeaderToolbar/HeaderToolbar";
import Image from "../Global/Image/Image";
import { Navigation, A11y } from "swiper";
import { Swiper, SwiperSlide } from "swiper/react/swiper-react";
import "swiper/swiper.scss";
import "swiper/modules/a11y/a11y.scss";
import "swiper/modules/navigation/navigation.scss";

import { Large, Small, Medium, IsMobileNav } from "../../media-queries/MediaQueries";
import ErrorComponent from "../Common/ErrorComponent/ErrorComponent";
import Message from "../SiteMessages/Message";
import { ALERT } from "../SiteMessages/constants";
import { Helmet, HelmetData } from "react-helmet-async";
import { useProductCache } from "../../hooks/useProductCache";
import { useAlertScreenReader } from "../Global/ScreenReaderAlert/hooks";
import sanitizeHtml from "sanitize-html";

import ProductImageZoom from "./ProductImageZoom/ProductImageZoom";
import ProductHeading from "./ProductHeading/ProductHeading";
import ProductFeatures from "./ProductFeatures/ProductFeatures";
import ProductVariations from "./ProductVariations/ProductVariations";
import ProductDescription from "./ProductDescription/ProductDescription";
import ProductTags from "./ProductTags/ProductTags";
import useModal from "../../hooks/useModal";

const MOBILEMEDIASLIDES = 5;
const TABLETMEDIASLIDES = 7;
const DESKTOPMEDIASLIDES = 7;

function ProductDetailsHeader(props) {
  const [activeMediaItem, setActiveMediaItem] = useState(0);
  const { isOpen: showVideoModal, toggle: toggleVideoModal, setIsOpen: setShowVideoModal } = useModal();
  const [variations, setVariations] = useState(false);

  const helmetData = new HelmetData({});
  const isMobileNav = IsMobileNav();

  const VIDEO_PREVIEW_IMAGE = "Video Preview Image";
  const VIDEO_LINK = "Product Vid";

  const page = useContext(BrPageContext);
  const component = useContext(BrComponentContext);
  let pfamLink = component.model.models.productCollectionLink;
  if(!pfamLink) pfamLink = window.location.href.substring(0,window.location.href.lastIndexOf('/'));
  const { channelDisplayName } = page.getComponent("globalproperties").getModels();
  const { productServletURL } = page.getComponent("header").getModels();
  const {setAlertMessage} = useAlertScreenReader();
  const scrollingTo = window.globalProperties.scrollingTo || "Scrolling to";
  const productReaderPrefix = window.globalProperties.productReaderPrefix || "View ";

  const switchProductImageAltTextPrefix = ((window?.globalProperties['switchProductImage']) || 'View product image');
  const { product } = page.getComponent().getModels();
  const { productData } = product ? product : {};
  
  // Fetch product variations with caching
  const variationCodes = productData?.variations || [];
  const { products: variationsData = [] } = useProductCache(
    variationCodes,
    productServletURL,
    null,
    null,
    !!(variationCodes.length && productServletURL)
  );

  useEffect(() => {
    if (variationsData && variationsData.length > 0) {
      setVariations(variationsData);
    }
  }, [variationsData]);

  //props did update aka linking to another page, even with same component
  //reset all state items
  useEffect(() => {
    setActiveMediaItem(0);
    setShowVideoModal(false);
  }, [props, setShowVideoModal]);

  if (!productData) return null;

  try {
    const {
      hideBreadcrumbs = false,
      productCatalogImageHeight: productCatalogImageHeightOverride,
      productCatalogImageWidth: productCatalogImageWidthOverride,
    } = component.getParameters();

    const { document: documentRef, breadcrumbs } = component.getModels();

    const productPrintHeightOverride = "50";
    const productPrintWidthOverride = "50";
    const productCatalogImageHeight = "75";
    const productCatalogImageWidth = "75";

    if (!product) return null;
    const document = page.getContent(documentRef);
    if (!document) return null;

    const {
      compatibleProductsLabel = null,
      newLabel = null,
      customEmbossingAvailableLabel = null,
      customPrintAvailableLabel = null,
      printLabel = null,
      recyclableLabel = null,
      recyclableTooltipTitle = null,
      recyclableTooltipText = null,
      recycledContentLabel = null,
      recycledContentTooltipText = null,
      recycledContentTooltipTitle = null,
      compostableLabel = null,
      compostableTooltipText = null,
      compostableTooltipTitle = null,
      renewableContentLabel = null,
      renewableContentTooltipText = null,
      renewableContentTooltipTitle = null,
      sourceReducedLabel = null,
      sourceReducedTooltipText = null,
      sourceReducedTooltipTitle = null,
      madeInTheUSALabel = null,
      madeInTheUSATooltipText = null,
      madeInTheUSATooltipTitle = null,
      pfasFreeLabel = null,
      pfasFreeTooltipTitle = null,
      pfasFreeTooltipText = null,
      proPlanetSustainableLabel = null,
      proPlanetSustainableTitle = null,
      proPlanetSustainableText = null,
      designedForRecyclingLabel = null,
      designedForRecyclingTitle = null,
      designedForRecyclingText = null,
      fullClaimFootnoteText = null,
      discontinuedProductText = null,
      zoomInLabel = null,
      zoomOutLabel = null,
      panLeftLabel = null,
      panRightLabel = null,
      panUpLabel = null,
      panDownLabel = null,
    } = document.getData();

    const isActiveProduct = productData["publishStatus"]?.toString().toLowerCase() !== "inactive";

    //set pdp page title
    if (productData.browserPageTitle && channelDisplayName) {
      // If both title and channelDisplayName exist
      window.document.title = productData.browserPageTitle + " - " + channelDisplayName;
    } else if (productData.browserPageTitle) {
      window.document.title = productData.browserPageTitle;
    }

    let videoUrl = null
    const buildMediaItem = (itemNum, image, altText, isVideoPreviewImage, isVideoUrl, imageType, productCode) => {
      if (!image && !isVideoPreviewImage) return null;
      if (isVideoUrl) {
        videoUrl = image
        return null;
      }
      return {
        itemNum: itemNum,
        imageUrl: image,
        altText: switchProductImageAltTextPrefix + " " + altText,
        isVideoPreviewImage: isVideoPreviewImage,
        isVideo: isVideoUrl,
        imageType: imageType,
        productCode: productCode
      };
    };

    let mediaItems = [];
    productData.digitalAssets.forEach((da, index) => {
      mediaItems.push(buildMediaItem(index, da.url, da.alternateText, VIDEO_PREVIEW_IMAGE === da.type, VIDEO_LINK === da.type, da.type, productData.productCode));
    });
    mediaItems = mediaItems.filter((item) => item !== null);

    //consolidate featured bullets
    const featuredBullets = [
      productData?.featureBullet1 ? productData.featureBullet1 : null,
      productData?.featureBullet2 ? productData.featureBullet2 : null,
      productData?.featureBullet3 ? productData.featureBullet3 : null,
    ];
    
    //productFeatures
    const productExtras = [
      productData?.isProPlanet
      ? {
          id: "isProPlanet",
          iconClass: `darticon-proPlanet_Leaf`,
          label: proPlanetSustainableLabel,
          title: proPlanetSustainableTitle,
          text: proPlanetSustainableText,
        }
      : null,

      productData?.isDesignedForRecycling
      ? {
          id: "isDesignedForRecycling",
          iconClass: `darticon-proPlanet_Leaf`,
          label: designedForRecyclingLabel,
          title: designedForRecyclingTitle,
          text: designedForRecyclingText,
        }
      : null,

      productData?.isRecyclable
        ? {
            id: "isRecyclable",
            iconClass: "dartrecycle",
            label: recyclableLabel,
            title: recyclableTooltipTitle,
            text: recyclableTooltipText,
          }
        : null,

      productData?.isRecycledContent
        ? {
            id: "isRecycledContent",
            iconClass: "dartrecycledcontent",
            label: recycledContentLabel,
            title: recycledContentTooltipTitle,
            text: recycledContentTooltipText,
          }
        : null,

      productData?.isCompostable
        ? {
            id: "isCompostable",
            iconClass: "dartcommerciallycompostable",
            label: compostableLabel,
            title: compostableTooltipTitle,
            text: compostableTooltipText,
          }
        : null,
      productData?.isRenewableContent
        ? {
            id: "isRenewableContent",
            iconClass: "dartrenewablecontent",
            label: renewableContentLabel,
            title: renewableContentTooltipTitle,
            text: renewableContentTooltipText,
          }
        : null,
      productData?.isSourceReduced
        ? {
            id: "isSourceReduced",
            iconClass: "dartsourcereduced",
            label: sourceReducedLabel,
            title: sourceReducedTooltipTitle,
            text: sourceReducedTooltipText,
          }
        : null,
      productData?.isMadeInUSA
        ? {
            id: "isMadeInUSA",
            iconClass: "dartmadeinusa",
            label: madeInTheUSALabel,
            title: madeInTheUSATooltipTitle,
            text: madeInTheUSATooltipText,
          }
        : null,
        productData?.isPFASFree
        ? {
            id: "isPFASFree",
            iconClass: "darticon-PFASfree",
            label: pfasFreeLabel,
            title: pfasFreeTooltipTitle,
            text: pfasFreeTooltipText,
          }
        : null,
    ];

    const outputHeadingArea = () => {
      return (
        <ProductHeading
          productData={productData}
          product={product}
          pfamLink={pfamLink}
          compatibleProductsLabel={compatibleProductsLabel}
          productReaderPrefix={productReaderPrefix}
          isMobileNav={isMobileNav}
          scrollingTo={scrollingTo}
          setAlertMessage={setAlertMessage}
        />
      );
    };

    const outputActiveImageArea = () => {
      return (
        <ProductImageZoom
          mediaItems={mediaItems}
          activeMediaItem={activeMediaItem}
          productCatalogImageHeightOverride={productCatalogImageHeightOverride}
          productCatalogImageWidthOverride={productCatalogImageWidthOverride}
          videoUrl={videoUrl}
          showVideoModal={showVideoModal}
          toggleVideoModal={toggleVideoModal}
          productData={productData}
          newLabel={newLabel}
          proPlanetSustainableLabel={proPlanetSustainableLabel}
          zoomInLabel={zoomInLabel}
          zoomOutLabel={zoomOutLabel}
          panLeftLabel={panLeftLabel}
          panRightLabel={panRightLabel}
          panUpLabel={panUpLabel}
          panDownLabel={panDownLabel}
        />
      );
    };

    const outputCustomizationMessage = () => {
      return (
        <>
          {productData?.isEmbossable && (
            <div className="pdp-header__customization">
              <span className="pdp-header__customization-text">
                {customEmbossingAvailableLabel}
              </span>
            </div>
          )}
          {productData?.isPrintable && (
            <div className="pdp-header__customization">
              <span className="pdp-header__customization-text">{customPrintAvailableLabel}</span>
            </div>
          )}
        </>
      );
    };

    const outputAltImagesSection = (isDesktop, maxMediaItems) => {
      if(mediaItems.length > maxMediaItems) {
        return (
          <ProductMediaThumbnailCarousel
            mediaItems={mediaItems}
            activeMediaItem={activeMediaItem}
            setActiveMediaItem={setActiveMediaItem}
            hasProductVideo={videoUrl}
            productCatalogImageHeight={productCatalogImageHeight}
            productCatalogImageWidth={productCatalogImageWidth}
            isVertical={isDesktop}
            toggleVideoModal={toggleVideoModal} />
        );
      }
      else {
        return (
          <ProductMediaThumbnailList
            mediaItems={mediaItems}
            activeMediaItem={activeMediaItem}
            setActiveMediaItem={setActiveMediaItem}
            hasProductVideo={videoUrl}
            productCatalogImageHeight={productCatalogImageHeight}
            productCatalogImageWidth={productCatalogImageWidth}
            toggleVideoModal={toggleVideoModal} />
        );
      }
    };


    return (
      <>
        {!isActiveProduct && (
          <Message
            message={discontinuedProductText}
            alertLevel={ALERT}
            canClose={false}
            canExpire={false}
          />
        )}

        <HeaderToolbar
          hideBreadcrumbs={hideBreadcrumbs}
          breadcrumbs={breadcrumbs}
        />
        <div className={`${page.isPreview() ? "has-edit-button" : ""} pdp-header`}>
          <BrManageContentButton content={document} />
          <Helmet helmetData={helmetData}>
            {productData.catalogImage && (
              <meta property="og:image" content={productData.catalogImage} />
            )}

            {productData.browserPageTitle && (
              <meta property="og:title" content={productData.browserPageTitle} />
            )}
            {productData.metaDescription && (
              <meta property="og:description" content={productData.metaDescription} />
            )}
            {productData.metaDescription && (
              <meta name="description" content={productData.metaDescription} />
            )}
          </Helmet>
          <div className="pdp-header__heading-wrapper">
            <Small>
              {outputHeadingArea()}
              {outputActiveImageArea()}
              {outputCustomizationMessage()}
              {outputAltImagesSection(false, MOBILEMEDIASLIDES)}
            </Small>
            <Medium>
              {outputHeadingArea()}
              {outputActiveImageArea()}
              {outputCustomizationMessage()}
              {outputAltImagesSection(false, TABLETMEDIASLIDES)}
            </Medium>
            <Large>
              <div className="pdp-header__imaging-wrapper">
                {outputAltImagesSection(true, DESKTOPMEDIASLIDES)}
                <div className="pdp-header__desktop-active-image-wrapper">
                  {outputActiveImageArea()}
                  {outputCustomizationMessage()}
                </div>
              </div>
            </Large>
            <div className="pdp-header__information-wrapper">
              <Large>{outputHeadingArea()}</Large>
              <ProductFeatures featuredBullets={featuredBullets} />
              <ProductVariations
                variations={variations}
                productData={productData}
                printLabel={printLabel}
                productPrintHeightOverride={productPrintHeightOverride}
                productPrintWidthOverride={productPrintWidthOverride}
              />
              {isActiveProduct && (
                <div className="pdp-header__add-wrapper">
                  <AddProductExpandable
                    productCode={product.productCode}
                    productName={sanitizeHtml(productData.childProductName)}
                    discontinued={false}
                    addToListAnalytics={{
                      event: "listAdd",
                      listType: "Main",
                      productName: `${sanitizeHtml(productData.childProductName)}`,
                    }}
                    removeFromListAnalytics={{
                      event: "removeFromList",
                      listType: "Main",
                      productName: `${sanitizeHtml(productData.childProductName)}`,
                    }}
                  />
                </div>
              )}
              <ProductDescription catalogDescription={productData?.catalogDescription} />
              <ProductTags productExtras={productExtras} fullClaimFootnoteText={fullClaimFootnoteText} />
            </div>
          </div>
        </div>
      </>
    );
  } catch (e) {
    console.error("ProductDetailHeader error", e);
    return <ErrorComponent error={e} componentName={"ProductDetailHeader"} />;
  }
}

function ProductMediaThumbnailCarousel({ mediaItems, activeMediaItem, setActiveMediaItem, hasProductVideo, productCatalogImageHeight, productCatalogImageWidth, isVertical, toggleVideoModal }) {
  return (
    <div
      className="pdp-header__carousel"
    >
      <Swiper
        navigation={{
          nextEl: ".pdp-header__swiper-button-next"
        }}
        modules={[Navigation, A11y]}
        slidesPerView={MOBILEMEDIASLIDES - 1}
        allowSlidePrev={true}
        allowSlideNext={true}
        loop={true}
        direction={isVertical ? 'vertical' : 'horizontal'}
        spaceBetween={10}
        a11y={{
          prevSlideMessage: (window.globalProperties && window.globalProperties["prevSlideMessage"]) || "Previous slide",
          nextSlideMessage: (window.globalProperties && window.globalProperties["nextSlideMessage"]) || "Next slide"
        }}
        breakpoints={{
          640: {
            slidesPerView: TABLETMEDIASLIDES - 1,
          },
          992: {
            slidesPerView: DESKTOPMEDIASLIDES - 1,
          },
        }}
        updateOnImagesReady={true}
        updateOnWindowResize={true}
        onAfterInit={function(swiper){
          swiper.$el.find(".swiper-slide-duplicate").find(".pdp-header__alt-image").attr("tabindex","-1");
        }}
      >
        {mediaItems.map((item, index) => {
          //check if video exists
          if (item.isVideoPreviewImage && !hasProductVideo) {
            return null;
          }
          return (
            <SwiperSlide key={index}>
              <button
                type="button"
                aria-pressed={activeMediaItem === item.itemNum ? "true" : "false"}
                title={item.altText ? item.altText : ""}
                className={`${activeMediaItem === item.itemNum ? "active" : ""} ${
                  item.isVideoPreviewImage ? "pdp-header__alt-image--video" : ""
                } pdp-header__alt-image`}
                onClick={item.isVideoPreviewImage ? () => setActiveMediaItem(item.itemNum) : toggleVideoModal}
              >
                {item.isVideoPreviewImage && (
                  <span className="pdp-header__alt-image-icon darticon-play"></span>
                )}
                {item.imageUrl && (
                  <Image
                    src={item.imageUrl}
                    alt={item.altText}
                    height={productCatalogImageHeight}
                    width={productCatalogImageWidth}
                    id={buildPDPHeaderImageIdValue(item, index)}
                  />
                )}
              </button>
            </SwiperSlide>
          );
        })}
      </Swiper>
      <button tabIndex="0" type="button" className="pdp-header__swiper-button-next">
        <span className="pdp-header__swiper-button-next__content">
          &rsaquo;
        </span>
      </button>
    </div>
  );
}

function ProductMediaThumbnailList({ mediaItems, activeMediaItem, setActiveMediaItem, hasProductVideo, productCatalogImageHeight, productCatalogImageWidth, toggleVideoModal }) {
  return (
    <ul className="pdp-header__alt-images-wrapper">
      {mediaItems.length > 0 && (
        <>
          {mediaItems.map((item, index) => {
            //check if video exists
            if (item.isVideoPreviewImage && !hasProductVideo) {
              return null;
            }
            const imageId = buildPDPHeaderImageIdValue(item, index)
            return (
              <li className="pdp-header__alt-image--list-item"
                  key={`alt-images__${index}`}>
                <button
                  type="button"
                  aria-pressed={activeMediaItem === item.itemNum ? "true" : "false"}
                  title={item.altText ? item.altText : ""}
                  className={`${activeMediaItem === item.itemNum ? "active" : ""} ${
                    item.isVideoPreviewImage ? "pdp-header__alt-image--video" : ""
                  } pdp-header__alt-image`}
                  onClick={() => setActiveMediaItem(item.itemNum)}
                  id={imageId ? "button-" + imageId : ""}
                >
                  {item.isVideoPreviewImage && (
                    <span className="pdp-header__alt-image-icon darticon-play"></span>
                  )}
                  {item.imageUrl && (
                    <Image
                      src={item.imageUrl}
                      alt={item.altText}
                      height={productCatalogImageHeight}
                      width={productCatalogImageWidth}
                      id={imageId}
                    />
                  )}
                </button>
              </li>
            );
          })}
        </>
      )}
    </ul>
  );
}

function buildPDPHeaderImageIdValue(mediaItem, index) {
  return `${mediaItem.productCode}-${mediaItem.imageType.replace(" ","-")}-Position-${(index + 1)}`
}

export default ProductDetailsHeader;
